/* eslint-disable */
import axios, { AxiosResponse } from 'axios';
import React, { useState, useEffect, useRef, useCallback, Fragment, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styles from './styles/style.module.scss';
import Plans from './layouts/plans';
import Footer from '../../layouts/footer';
import Button from '../../components/ui/button/Button';
import { useStore } from '../../store/store';
import { ReactComponent as StarSvg } from '../../assets/images/star.svg';
import { ReactComponent as HandSvg } from './styles/hand.svg';
import { debounce } from 'lodash';
import { selectProducts } from 'core/store/billing/selectors';
import { useRemoteConfig } from 'core/hooks/use-remote-config';
import { isFoodzeeBackend } from '@web-solutions/react-billing';
import { getLocalizedText } from 'core/utils/localized';
import { t } from '@web-solutions/module-localization';
import getLocale from '../../../localization/get-locale';
import { Title } from 'src/onboarding/components/typography/Title/Title';
import { Text } from 'src/onboarding/components/typography/Text/Text';
import { Caption } from 'src/onboarding/components/typography/Caption/Caption';
import { getEnvUrl } from 'src/onboarding/helpers/getEnvUrl';
import { Allergies, UserPayload, UserResponse } from 'src/onboarding/types/api';

import video from './styles/personalPlan2.mp4';
import MealPlanContainer from './layouts/mealPlanContainer';
import { EmailPopup } from 'src/onboarding/components/ui/email-popup/EmailPopup';
import { GiftPopup } from 'src/onboarding/components/ui/gift-popup/GiftPopup';
import GoalGraphPaywall from './layouts/goalGraph';
import { VideoComponent } from './layouts/videoComponent';
import { YouWillGetComponent } from './layouts/youWillGet';
import { CustomersComponent } from './layouts/customers';
import BeforeAfterCard from './layouts/beforeAfterCard';
import HealthDashboardCard from './layouts/healthDashboardCard';
import { BenefitsComponent } from './layouts/benefits';
import { ReachYourGoalComponent } from './layouts/reachYourGoal';
import { StatsComponent } from './layouts/stats';
import { GuideContainer } from './layouts/guide';
import { TryFoodzeeComponent } from './layouts/tryFoodzee';
import { JoinedPeopleComponent } from './layouts/joinedPeople';
import { ReportComponent } from './layouts/report';
import MealPlanContainerPizza from './layouts/mealPlanContainerPizza';
import BeforeAfterCardVine from './layouts/beforeAfterCardVine';
import { GetSelfieReportPopup } from 'src/onboarding/components/ui/GetSelfieReportPopup/GetSelfieReportPopup';
import { applyFoodzeeAuthTokens } from 'src/onboarding/helpers/applyFoodzeeAuthTokens';
import { useAnonymousUser } from 'src/onboarding/hooks/useAnonymousUser';
import { useCheckoutStatusPolling } from 'src/onboarding/hooks/useCheckoutStatusPolling';
import CheckoutProcessingOverlay from 'src/onboarding/components/ui/checkout-processing-overlay';
import Analytics from '@web-solutions/module-analytics';
import { STRIPE_INTRO_PAYMENT } from 'core/utils/stripe-payment';
import {
  PAYMENT_SYSTEM_PROJECT,
  createCheckoutSession,
  getCheckoutReturnUrls,
  storeFunnelSearch,
} from 'src/onboarding/helpers/checkoutApi';

declare const gtag: Function;

// Same heuristic the core PaymentProcessor uses to tell the one-time intro/trial
// product apart from the subscription products in the billing list. The intro
// product's `id` is the Stripe trial price id (trial_payment_price_id).
const isStripeIntroProduct = (product?: any) =>
  Boolean(
    product?.isOneTimePurchase ||
      product?.id === STRIPE_INTRO_PAYMENT.priceId ||
      product?.period === 'ONETIME'
  );

const EMAIL_POPUP_KEY = 'email_popup_shown';
const GIFT_POPUP_KEY = 'gift_popup_shown';
const GET_SELFIE_REPORT_POPUP_KEY = 'get_selfie_report_popup_shown';

interface PlansRef {
  onPayment: () => void;
}

function addDaysToDate(days: string): string {
  const daysNumber = parseInt(days, 10);
  if (isNaN(daysNumber)) {
    return '';
  }

  const currentDate = new Date();
  currentDate.setDate(currentDate.getDate() + daysNumber);

  // The funnel language, not <html lang>: that attribute is only set when the
  // localizationEnabled flag is on, so de/it/es/fr still got "September 24, 2026".
  const locale = getLocale();

  const formatter = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const formattedDate = formatter.format(currentDate);
  console.log('formattedDate ', formattedDate);
  return formattedDate;
}

const DynamicPersonalPlan = () => {
  const dispatch = useDispatch();
  const locale = getLocale();
  const childRef = useRef<PlansRef>(null);
  const pageRef = useRef(null);
  const state = useStore((state) => state);
  const {
    setPage,
    currentPage,
    email,
    born: age,
    gender,
    preferences: dietary,
    height,
    weight,
    setUserId,
    savedOnboarding,
    setSavedOnboarding,
    name: userName,
    existTimeoutId,
    bodyType,
    bodyScanError,
    bodyImageUrl,
    bmi,
    bodyImageFile,
    userId,
    authTokens,
  } = state;

  const [activeProduct, setActiveProduct] = useState<any>(null);

  const productsObj = useSelector(selectProducts);
  const apiAuthSource = useSelector((s: any) => s.profile?.apiAuth?.source);
  const {
    specialOffer,
    products,
    routeGroups: flow,
    showEmailPopup,
    showGiftPopup,
    showBeforeAfterOnPaywall,
    showPlanDescription,
    timerDesign,
    personalPlanPage,
    scrollEnabled,
    hideTimerOnScrollUp,
    showGetSelfieReportPopup,
    paymentSystem,
    collectEmailAfterPayment,
    //@ts-ignore — useStripeCheckout lives on the core RemoteConfig type
    useStripeCheckout,
  } = useRemoteConfig();
  const isStripePayment = paymentSystem === 'stripe';
  // Both Stripe and TailoredPay talk to the Foodzee backend, which needs the
  // 'foodzee'-sourced auth token and the onboarding data saved before checkout.
  // Anything genuinely Stripe-SDK-specific keeps using `isStripePayment`.
  const isFoodzeeBillingPayment = isFoodzeeBackend(paymentSystem);
  // A/B hosted Stripe Checkout: gated on the flag AND the Stripe payment system.
  const isStripeCheckout = isStripePayment && !!useStripeCheckout;

  // Stripe deferred-email flow: create the anonymous user (email = null) as soon
  // as the user lands on the personal-plan (paywall) page, so checkout needs no
  // email. No-op unless `collectEmailAfterPayment` is enabled and Stripe is used.
  useAnonymousUser();

  // Email is deferred to the Stripe-hosted page (no up-front popup) ONLY when
  // `collectEmailAfterPayment` is on. With it off we still collect the email up
  // front via the popup — even in hosted Checkout — and only then redirect.
  const deferEmailToStripe = isStripeCheckout && collectEmailAfterPayment;

  // Deferred-email flow: there's no up-front email page to fire the FB and TikTok
  // pixel CompleteRegistration, so fire it once when the user lands on the
  // personal-plan page. The email-after-payment page no longer fires it.
  const completeRegistrationSent = useRef(false);
  useEffect(() => {
    if (!collectEmailAfterPayment || completeRegistrationSent.current) return;
    completeRegistrationSent.current = true;
    // @ts-ignore
    window.fbq?.('track', 'CompleteRegistration');
    // @ts-ignore
    window.ttq?.track?.('CompleteRegistration');
  }, [collectEmailAfterPayment]);

  // Stripe hosted-Checkout return: when the user comes back from Stripe, App
  // routes here with `checkoutSessionId` set; this polls the session status and
  // shows the blocking billing-status overlay until it resolves (then routes to
  // the upsell / email page, or hides on failure). No-op on a normal visit.
  const {
    visible: checkoutStatusVisible,
    timedOut: checkoutTimedOut,
    retry: retryCheckoutStatus,
  } = useCheckoutStatusPolling();

  const isEmailPopupPending =
    showEmailPopup &&
    !email &&
    !userId &&
    !deferEmailToStripe &&
    !sessionStorage.getItem(EMAIL_POPUP_KEY);

  const [showDownSaleModalOnce, setShowDownSaleModalOnce] = useState(false);
  const [isDownSaleModalOpen, setIsDownSaleModalOpen] = useState(false);
  const [isEmailPopupOpen, setIsEmailPopupOpen] = useState(
    isEmailPopupPending
  );
  const canShowDownSaleModal = !isEmailPopupOpen && !isEmailPopupPending;
  const [isGetSelfieReportPopupOpen, setIsGetSelfieReportPopupOpen] = useState(false);
  const [isGiftPopupOpen, setIsGiftPopupOpen] = useState(false);

  useEffect(() => {
    if (isEmailPopupOpen) {
      const scrollY = window.scrollY;

      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';

      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';

        window.scrollTo(0, scrollY);
      };
    }
  }, [isEmailPopupOpen]);

  useEffect(() => {
    if (isEmailPopupPending) {
      setIsEmailPopupOpen(true);
      return;
    }

    // EmailPopup is still on screen — `email` becomes truthy on every keystroke
    // which flips `isEmailPopupPending` to false; gate the chained popups on
    // the actual close so GiftPopup doesn't appear on top of EmailPopup.
    if (isEmailPopupOpen) return;

    if (
      showGetSelfieReportPopup &&
      !sessionStorage.getItem(GET_SELFIE_REPORT_POPUP_KEY)
    ) {
      setIsGetSelfieReportPopupOpen(true);
    } else if (showGiftPopup && !sessionStorage.getItem(GIFT_POPUP_KEY)) {
      setIsGiftPopupOpen(true);
    }
  }, [email, isEmailPopupOpen, isEmailPopupPending, showGetSelfieReportPopup, showGiftPopup, userId]);

  useEffect(() => {
    if (
      currentPage === 'personal-plan-dynamic' &&
      isEmailPopupPending &&
      !isEmailPopupOpen
    ) {
      setIsEmailPopupOpen(true);
    }
  }, [currentPage, isEmailPopupOpen, isEmailPopupPending]);

  const handleEmailPopupClose = () => {
    setIsEmailPopupOpen(false);
    sessionStorage.setItem(EMAIL_POPUP_KEY, 'true');

    if (showGetSelfieReportPopup && !sessionStorage.getItem(GET_SELFIE_REPORT_POPUP_KEY)) {
      setIsGetSelfieReportPopupOpen(true);
    }
    else if (showGiftPopup && !sessionStorage.getItem(GIFT_POPUP_KEY)) {
      setIsGiftPopupOpen(true);
    }
  };

  const handleGetSelfieReportPopupClose = () => {
    setIsGetSelfieReportPopupOpen(false);
    sessionStorage.setItem(GET_SELFIE_REPORT_POPUP_KEY, 'true');

    if (showGiftPopup && !sessionStorage.getItem(GIFT_POPUP_KEY)) {
      setIsGiftPopupOpen(true);
    }
  };

  const handleGiftPopupClose = () => {
    setIsGiftPopupOpen(false);
    sessionStorage.setItem(GIFT_POPUP_KEY, 'true');
  };

  const { apiBaseUrl } = getEnvUrl();

  useEffect(() => {
    gtag('event', 'conversion', {
      send_to: 'AW-17149973324/gwbSCMu_yP0aEMym3_E_',
      value: 1.5,
      currency: 'USD',
    });
  }, []);

  useEffect(() => {
    return () => {
      clearTimeout(existTimeoutId);
    };
  }, [existTimeoutId]);

  useEffect(() => {
    const videoEl = document.createElement('video');
    videoEl.style.display = 'none';
    videoEl.preload = 'auto';
    videoEl.muted = true;
    videoEl.src = video;

    document.body.appendChild(videoEl);

    return () => {
      if (document.body.contains(videoEl)) {
        document.body.removeChild(videoEl);
      }
    };
  }, [video]);

  console.log('products ', products);
  const productDescription = showDownSaleModalOnce
    ? {
        name: specialOffer.products[0]?.name,
        plan_end_days: addDaysToDate(specialOffer.products[0]?.plan_end_days),
        subTitle: specialOffer.products[0]?.subTitle[locale],
        title: specialOffer.products[0]?.title[locale],
        price: specialOffer.products[0]?.priceStartTrial.price,
      }
    : {
        name: products[0]?.name,
        plan_end_days: addDaysToDate(products[0]?.priceStartTrial.plan_end_days),
        subTitle: products[0]?.subTitle[locale],
        title: products[0]?.title[locale],
        price: products[0]?.priceStartTrial.price[locale],
      };
  const { name, plan_end_days, subTitle, title, price } = productDescription;

  const targetRef = useRef(null);

  const scrollToTarget = () => {
    if (targetRef.current) {
      targetRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const ensureOnboardingData = useCallback(
    async () => {
      let effectiveUserId = userId;
      const needsStripeAuth = isFoodzeeBillingPayment && !authTokens?.accessToken;

      if (isFoodzeeBillingPayment && (!effectiveUserId || needsStripeAuth) && !email) {
        if (showEmailPopup) {
          sessionStorage.removeItem(EMAIL_POPUP_KEY);
          setIsEmailPopupOpen(true);
        }
        return false;
      }

      try {
        if (!effectiveUserId || needsStripeAuth) {
          const payload: UserPayload = {
            email,
          };

          const response: AxiosResponse<UserResponse> = await axios.post(
            `${apiBaseUrl}/v2/user`,
            payload
          );

          if (response.status === 201) {
            effectiveUserId = response.data.uid;
            setUserId(effectiveUserId);
            const billingInit = applyFoodzeeAuthTokens(response.data, dispatch, {
              reinitBilling: true,
              paymentSystem,
            });

            if (isFoodzeeBillingPayment && billingInit && typeof (billingInit as any).then === 'function') {
              await billingInit;
            }
          }
        }

        if (!savedOnboarding && effectiveUserId) {
          const filteredState = Object.fromEntries(
            Object.entries({ ...state }).filter(
              ([key, value]) => typeof value !== 'function' && key !== 'faceScanBase64'
            )
          );

          await axios.post(`${apiBaseUrl}/onboarding`, {
            json: JSON.stringify(filteredState),
            user_id: effectiveUserId,
          });

          setSavedOnboarding(true);
        }

        return true;
      } catch (error) {
        console.error('Error:', error);
        return false;
      }
    },
    [apiBaseUrl, authTokens?.accessToken, dispatch, email, gender, height, isFoodzeeBillingPayment, paymentSystem, savedOnboarding, setSavedOnboarding, setUserId, showEmailPopup, state, userId, weight]
  );

  // On a fresh page load the in-memory billing auth headers and the Stripe
  // billing init are lost, but the persisted store may still hold the Foodzee
  // auth tokens (the user already submitted their email earlier). Re-apply them
  // once so Stripe is initialized again without forcing a re-entry. If there
  // are no tokens the user hasn't authenticated yet — creating the user happens
  // only on email submit (EmailPopup) or on the payment click, never here.
  const billingReinitDone = useRef(false);

  useEffect(() => {
    if (!isFoodzeeBillingPayment || billingReinitDone.current) {
      return;
    }

    if (authTokens?.accessToken) {
      billingReinitDone.current = true;
      applyFoodzeeAuthTokens(
        {
          accessToken: authTokens.accessToken,
          refreshToken: authTokens.refreshToken,
          uid: authTokens.userId || userId,
        },
        dispatch,
        { reinitBilling: true, paymentSystem }
      );
    }
  }, [authTokens, userId, dispatch, isFoodzeeBillingPayment, paymentSystem]);

  // The app bootstrap dispatches its own auth() (source 'billing') as a
  // fire-and-forget on load — it resolves asynchronously and overwrites our
  // 'foodzee' billing auth. For Stripe, billing requests (createCustomer →
  // trial → subscribe) require source 'foodzee' or they reject with
  // "Missing billing auth token". Whenever the source drifts away from
  // 'foodzee' while we hold Foodzee tokens, re-assert it. The bootstrap auth()
  // overwrites once, so this flips it back once and then stays put.
  useEffect(() => {
    if (!isFoodzeeBillingPayment || !authTokens?.accessToken || apiAuthSource === 'foodzee') {
      return;
    }

    applyFoodzeeAuthTokens(
      {
        accessToken: authTokens.accessToken,
        refreshToken: authTokens.refreshToken,
        uid: authTokens.userId || userId,
      },
      dispatch,
      { reinitBilling: false, paymentSystem }
    );
  }, [apiAuthSource, authTokens, userId, dispatch, isFoodzeeBillingPayment, paymentSystem]);

  // Hosted Stripe Checkout: ensure an authenticated (anonymous) user exists,
  // persist the onboarding answers, then create a Checkout Session and redirect
  // to Stripe. Card / Apple Pay / Google Pay / Link and email are collected on
  // the Stripe-hosted page.
  const startStripeCheckout = useCallback(async (productOverride?: any) => {
    // Analytics: mirror the card-payment funnel (modal_payment_open →
    // modal_payment_submit → ecommerce_purchase). The hosted Checkout has no
    // in-app payment form, so `open` fires when the flow starts (button click);
    // `submit` fires on the return leg (in useCheckoutStatusPolling) once the
    // user has actually entered and submitted their details on Stripe and the
    // session resolves to `success`. Amount/currency reflect the intro (today's)
    // charge, with a fallback to the shared intro constant.
    const introForAnalytics = productsObj?.find((p: any) => isStripeIntroProduct(p));
    const paymentEventParams = {
      productId: introForAnalytics?.id || STRIPE_INTRO_PAYMENT.productId,
      value:
        +introForAnalytics?.trialPriceAmount ||
        +introForAnalytics?.amount ||
        STRIPE_INTRO_PAYMENT.amount,
      currency: introForAnalytics?.currency || STRIPE_INTRO_PAYMENT.currency,
    };
    Analytics.trackEvent('modal_payment', 'open', paymentEventParams);

    let accessToken =
      authTokens?.accessToken || useStore.getState().authTokens?.accessToken;
    let effectiveUserId = userId || useStore.getState().userId;

    // The anonymous user is normally created on page load (useAnonymousUser),
    // but create it here too in case the click beats that request.
    if (!accessToken) {
      try {
        const response: AxiosResponse<UserResponse> = await axios.post(
          `${apiBaseUrl}/v2/user`
        );
        if (response.status === 201) {
          effectiveUserId = response.data.uid;
          setUserId(effectiveUserId);
          const billingInit = applyFoodzeeAuthTokens(response.data, dispatch, {
            reinitBilling: true,
            paymentSystem,
          });
          if (billingInit && typeof (billingInit as any).then === 'function') {
            await billingInit;
          }
          accessToken =
            (response.data as any).accessToken ||
            useStore.getState().authTokens?.accessToken;
        }
      } catch (error) {
        Analytics.trackEvent('checkout', 'user_create_error');
        console.error('Checkout: failed to create user', error);
        return;
      }
    }

    if (!accessToken) {
      return;
    }

    // Persist the quiz answers before leaving the app (so the report/profile has
    // data), mirroring the non-checkout flow's onboarding save.
    if (!savedOnboarding && effectiveUserId) {
      try {
        const filteredState = Object.fromEntries(
          Object.entries({ ...state }).filter(
            ([key, value]) => typeof value !== 'function' && key !== 'faceScanBase64'
          )
        );
        await axios.post(`${apiBaseUrl}/onboarding`, {
          json: JSON.stringify(filteredState),
          user_id: effectiveUserId,
        });
        setSavedOnboarding(true);
      } catch (error) {
        // Non-fatal — proceed to checkout even if the answers didn't persist.
        console.error('Checkout: failed to save onboarding', error);
      }
    }

    // The trial price id comes from the one-time intro product in the resolved
    // billing list (its `id` is the Stripe price id); fall back to the shared
    // intro constant if it isn't present.
    const introProduct = productsObj?.find((p: any) => isStripeIntroProduct(p));

    // The subscription price id is the id of the NON-one-time product. Prefer the
    // selected/clicked plan, but only if it isn't the intro product itself; then
    // the default non-intro plan, then the first non-intro plan.
    const selectedProduct = productOverride || activeProduct;
    const subscriptionProduct =
      (selectedProduct && !isStripeIntroProduct(selectedProduct) ? selectedProduct : null) ||
      productsObj?.find((p: any) => !isStripeIntroProduct(p) && p?.default) ||
      productsObj?.find((p: any) => !isStripeIntroProduct(p)) ||
      selectedProduct ||
      {};

    const { success_url, cancel_url } = getCheckoutReturnUrls();

    const payload = {
      payment_system_project: PAYMENT_SYSTEM_PROJECT,
      trial_payment_price_id: introProduct?.id || STRIPE_INTRO_PAYMENT.priceId,
      subscription_price_id:
        subscriptionProduct?.id || subscriptionProduct?.stripeId,
      subscription_product_id: subscriptionProduct?.productCode || undefined,
      product_code: introProduct?.productCode || STRIPE_INTRO_PAYMENT.productCode,
      trial_period_days: String(
        subscriptionProduct?.trialDays || STRIPE_INTRO_PAYMENT.trialPeriodDays
      ),
      success_url,
      cancel_url,
    };

    try {
      Analytics.trackEvent('checkout', 'create_session');
      const session = await createCheckoutSession(payload, accessToken);
      if (session?.session_url) {
        // Note: `modal_payment_submit` is intentionally NOT fired here. On the
        // hosted-Checkout flow the user still hasn't entered/submitted their
        // payment details at this point — they only do that on the Stripe page.
        // The `submit` event is fired on the return leg, in
        // useCheckoutStatusPolling once the session resolves to `success`.
        // Persist the funnel query (e.g. `?flow=...`) so it survives the Stripe
        // round-trip — the return URL only carries `?session_id`.
        storeFunnelSearch();
        window.location.href = session.session_url;
      } else {
        Analytics.trackEvent('checkout', 'create_session_empty');
      }
    } catch (error) {
      Analytics.trackEvent('checkout', 'create_session_error');
      console.error('Checkout: failed to create session', error);
    }
  }, [
    activeProduct,
    apiBaseUrl,
    authTokens?.accessToken,
    dispatch,
    paymentSystem,
    productsObj,
    savedOnboarding,
    setSavedOnboarding,
    setUserId,
    state,
    userId,
  ]);

  const payment = async () => {
    if (isStripeCheckout) {
      await startStripeCheckout();
      return;
    }

    if (!isFoodzeeBillingPayment) {
      if (showEmailPopup && !email) {
        sessionStorage.removeItem(EMAIL_POPUP_KEY);
        setIsEmailPopupOpen(true);
        return;
      }

      childRef.current?.onPayment();
      return;
    }

    // The Foodzee backend needs the user (and its 'foodzee' token) to exist and
    // the onboarding answers to be stored before the payment can be created.
    const isReady = await ensureOnboardingData();

    if (!isReady) {
      return;
    }

    childRef.current?.onPayment();
  };

  const setOnboardingData = useCallback(
    debounce(() => {
      ensureOnboardingData();
    }, 500),
    [ensureOnboardingData]
  );

  useEffect(() => {
    if (isFoodzeeBillingPayment && showEmailPopup && isEmailPopupOpen) {
      return;
    }

    if (isFoodzeeBillingPayment) {
      if (email && !savedOnboarding && !isEmailPopupOpen) {
        setOnboardingData();
      }
    } else if (!savedOnboarding && !showEmailPopup) {
      setOnboardingData();
    }

    return () => {
      setOnboardingData.cancel();
    };
  }, [email, isEmailPopupOpen, isFoodzeeBillingPayment, savedOnboarding, setOnboardingData, showEmailPopup]);

  const sectionComponents = {
    button: (
      <Button
        className={styles.startTrialBtn}
        title={getLocalizedText(personalPlanPage.buttonTitle, locale)}
        onClick={
          // In Checkout mode the button always redirects to Stripe (no scroll).
          isStripeCheckout
            ? payment
            : //@ts-ignore
              scrollEnabled && personalPlanPage?.sections?.includes('try-foodzee')
              ? scrollToTarget
              : payment
        }
      />
    ),
    'payment-button': (
      <Button
        className={styles.startTrialBtn}
        title={
          getLocalizedText(personalPlanPage.paymentButtonTitle, locale) ||
          getLocalizedText(personalPlanPage.buttonTitle, locale)
        }
        onClick={payment}
      />
    ),
    'goal-graph':
      showBeforeAfterOnPaywall && flow.flat().includes('body-type') && bodyType !== 'Slim' ? (
        <BeforeAfterCard />
      ) : (
        <GoalGraphPaywall />
      ),
    'health-dashboard': flow?.flat().includes('full-body-photo') &&
      !bodyScanError &&
      bodyImageUrl?.length > 0 && <HealthDashboardCard url={bodyImageFile} bmi={bmi} />,
    'meal-plan': <MealPlanContainer />,
    'meal-plan-pizza': <MealPlanContainerPizza />,
    'before-after-vine': <BeforeAfterCardVine />,
    stats: <StatsComponent />,
    plans: (
      <>
        <Plans
          ref={childRef}
          showDownSaleModalOnce={showDownSaleModalOnce}
          setShowDownSaleModalOnce={setShowDownSaleModalOnce}
          scrollToTarget={scrollToTarget}
          onActiveProductChange={setActiveProduct} // ← новый проп
          onCheckoutRedirect={isStripeCheckout ? startStripeCheckout : undefined}
          canShowDownSaleModal={canShowDownSaleModal}
          onDownSaleModalToggle={setIsDownSaleModalOpen}
          //@ts-ignore
          hasTryFoodzeeSection={personalPlanPage?.sections?.includes('try-foodzee')}
        />
        <span className={styles.privacy}>
          {t('onboarding.personalPlan.plan.accept')}{' '}
          <a onClick={() => setPage('privacy-policy')}>
            {t('onboarding.personalPlan.plan.privacy')}
          </a>
          , <a onClick={() => setPage('terms-of-use')}>{t('onboarding.personalPlan.plan.terms')}</a>
        </span>
        {showPlanDescription && (
          <span className={styles.privacy}>
            {t('onboarding.personalPlan.plan.planExpires')} {plan_end_days}.{' '}
            {t('onboarding.personalPlan.plan.harged')} {subTitle}{' '}
            {t('onboarding.personalPlan.plan.for')} {title},{' '}
            {t('onboarding.personalPlan.plan.then')} {price}.{' '}
            {t('onboarding.personalPlan.plan.last')}
          </span>
        )}
      </>
    ),
    video: <VideoComponent />,
    guide: <GuideContainer />,
    'try-foodzee': <TryFoodzeeComponent ref={targetRef} product={productDescription} />,
    'joined-people': <JoinedPeopleComponent />,
    report: <ReportComponent />,
    'you-will-get': <YouWillGetComponent />,
    benefits: <BenefitsComponent />,
    'reach-your-goal': <ReachYourGoalComponent />,
    'customers-review': <CustomersComponent />,
  };

  const renderHighlightedTitle = (titleText: string) => {
    if (!titleText) return null;

    const parts = titleText.split(/\*\*(.*?)\*\*/);

    return parts.map((part, index) =>
      index % 2 === 1 ? (
        <span key={index} style={{ color: '#white' }}>
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  // проверять сам объект бессмысленно — он всегда truthy, скрывать блок нужно по тексту
  const pageTitle = getLocalizedText(personalPlanPage.title, locale);
  const pageSubTitle = getLocalizedText(personalPlanPage.subTitle, locale);

  return (
    <>
      <div className={styles.page} ref={pageRef}>
        <div className={styles.container}>
          <div className={styles.bodyContainer}>
            {pageTitle && (
              <Title tag='h4' className={styles.title}>
                {renderHighlightedTitle(pageTitle)}
              </Title>
            )}
            {pageSubTitle && (
              <Text size='m' medium style={{ color: '#3F3F3F', marginBottom: '16px' }}>
                {pageSubTitle}
              </Text>
            )}

            {personalPlanPage.sections?.map((section, index) => (
              <Fragment key={`${section}-${index}`}>{sectionComponents[section]}</Fragment>
            ))}

            {/* <Caption size='m' className={styles.refunds}>
              {t('onboarding.mealPlanContainer.refunds')}
            </Caption> */}
            {/* <Separator /> */}

            <div className={styles.moneyBack}>
              {personalPlanPage.moneyBack?.title?.[locale] && (
                <>
                  <HandSvg />
                  <h2>{personalPlanPage.moneyBack.title[locale]}</h2>
                </>
              )}
              {personalPlanPage.moneyBack?.sub1?.[locale] && (
                <span className={styles.firsBody}>
                  {personalPlanPage.moneyBack.sub1[locale]}
                </span>
              )}
              {personalPlanPage.moneyBack?.sub2?.[locale] && (
                <span className={styles.secondBody}>
                  {personalPlanPage.moneyBack.sub2[locale]}
                </span>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>

      <Timer
        payment={
          isStripeCheckout
            ? payment
            : //@ts-ignore
              personalPlanPage?.sections?.includes('try-foodzee') && scrollEnabled
              ? scrollToTarget
              : payment
        }
        isDownSaleModalOpen={isDownSaleModalOpen}
        timerDesign={timerDesign}
        hideTimerOnScrollUp={hideTimerOnScrollUp}
      />
      {showEmailPopup && <EmailPopup isOpen={isEmailPopupOpen} onClose={handleEmailPopupClose} />}

      {showGetSelfieReportPopup && (
        <GetSelfieReportPopup
          isOpen={isGetSelfieReportPopupOpen}
          onClose={handleGetSelfieReportPopupClose}
        />
      )}

      {showGiftPopup && <GiftPopup isOpen={isGiftPopupOpen} onClose={handleGiftPopupClose} />}

      <CheckoutProcessingOverlay
        visible={checkoutStatusVisible}
        timedOut={checkoutTimedOut}
        onRetry={retryCheckoutStatus}
      />
    </>
  );
};

const Timer = ({ payment, isDownSaleModalOpen, timerDesign, hideTimerOnScrollUp }) => {
  const [timeLeft, setTimeLeft] = useState(595);
  const [isTimerVisible, setIsTimerVisible] = useState(true);
  // Управляется параметром hideTimerOnScrollUp: при скролле вниз таймер
  // показывается, при скролле вверх — скрывается.
  const [isHiddenByScroll, setIsHiddenByScroll] = useState(false);
  const locale = getLocale();

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(timer);
          setIsTimerVisible(false);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!hideTimerOnScrollUp) {
      setIsHiddenByScroll(false);
      return;
    }

    // При включённом параметре таймер скрыт при открытии страницы
    // и появляется только после скролла вниз.
    setIsHiddenByScroll(true);

    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      // Игнорируем микро-движения и rubber-band эффект при overscroll.
      if (Math.abs(currentScrollY - lastScrollY) < 5) {
        return;
      }
      // Скролл вниз — показываем, скролл вверх — скрываем.
      setIsHiddenByScroll(currentScrollY < lastScrollY);
      lastScrollY = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => window.removeEventListener('scroll', handleScroll);
  }, [hideTimerOnScrollUp]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  return (
    <>
      {isTimerVisible && !isDownSaleModalOpen && !isHiddenByScroll && (
        <>
          <div className={styles.timerSpacer} />
          <div className={styles.timer}>
            <div className={styles.time}>
              <span className={styles.timeExpire}>{getLocalizedText(timerDesign.text, locale)}</span>
              <span style={{ color: '#E768B0' }}>{formatTime(timeLeft)}</span>
            </div>
            <Button title={getLocalizedText(timerDesign.buttonTitle, locale)} onClick={payment} />
          </div>
        </>
      )}
    </>
  );
};

export default DynamicPersonalPlan;
